import { SockMessageSend } from './message-send-event.model';
/* A library for working with SockJS websockets.
 * This is somewhat based on rxjs-websockets but with a lot of customisations
 * for freelancer.
 */
import { Injectable, Inject } from '@angular/core';
import { Auth } from '@shared/auth';
import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs/Observable';
import { Observer } from 'rxjs/Observer';
import { Subscription } from 'rxjs/Subscription';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Store } from '../store';
import { MultiSet } from './multiset';

import {
  generateChannelSubMessage,
  generateHeader,
  generateUserSub,
  generateUserUnsub,
  Socket
} from './sock-js';
import {
  isOnlineOfflineMessageEvent,
  isSockMessageServerEvent,
  SockServerEvent,
  SockMessageServerEvent,
} from './server-message-event.model';
import { DATASTORE_CONFIG } from '../datastore.config';
import { DatastoreConfig } from '../datastore.interface';
import { RootModel } from '../root.model';

interface UserSub {
  type: 'sub' | 'unsub';
  id: number;
}

@Injectable()
export class WebSocketService {
  private sock: Socket;
  private connectionStatus = new BehaviorSubject<number>(0);

  public fromServerStream = new Subject<SockServerEvent>();
  private onlineOfflineSubs = new Subject<UserSub>();
  private onlineOfflineSubscription: Subscription;

  private open = false;
  private channels = new Set<number>();
  private userSubscriptions = new MultiSet<number>();

  constructor(
    @Inject(DATASTORE_CONFIG) private datastoreConfig: DatastoreConfig,
    private auth: Auth,
    private store: Store<RootModel>,
  ) {
    this.create();
    this.subscribeChannels([3]); // FIXME: Call this properly.
  }

  private create() {
    this.open = false;
    this.sock = new Socket(this.datastoreConfig.webSocketUrl);

    this.auth
      .distinctUntilChanged()
      .subscribe(auth => {
        this.open = false
        const closed = () => {
          if (!this.open) {
            return;
          }

          this.connectionStatus.next(this.connectionStatus.getValue() - 1);
          this.onlineOfflineSubscription.unsubscribe();
          this.open = false;
        }

        this.sock.onopen = () => {
          this.open = true;
          this.connectionStatus.next(this.connectionStatus.getValue() + 1);
          this.sendEvent(generateHeader(auth));
          this.subscribeToUserEvents();
        }

        // Only send events with actual messages, not handshaking messages.
        this.sock.onmessage = event => {
          const decodedEvent = JSON.parse(event.data);
          if (isSockMessageServerEvent(decodedEvent)) {
            this.processEvent(decodedEvent, auth.uid);
          }
        }

        this.sock.onclose = (event: CloseEvent) => {
          closed();
          if (event.wasClean) {
            this.fromServerStream.complete();
          } else {
            this.fromServerStream.error(new Error(event.reason));
          }
        }

        return () => {
          if (this.sock) {
            closed();
            this.sock.close();
          }
        }
      });
  }

  private sendEvent(message: SockMessageSend) {
    if (this.open) {
      this.sock.send(message);
    } else {
      console.error('Tried sending a message to a closed websocket.');
    }
  }

  private processEvent(event: SockMessageServerEvent, userId: number) {
    if (isOnlineOfflineMessageEvent(event)) {
      Object.entries(event.body.data)
        .map(([id, status]) => ({ userId: id, status }))
        .forEach(data => this.store.dispatch({
          type: 'WS_MESSAGE',
          payload: {
            data,
            type: 'ONLINE_OFFLINE',
            userId: String(userId)
          }
        }));
    }

    switch (event.body.type) {
      case 'project':
        this.store.dispatch({
          type: 'WS_MESSAGE',
          payload: {
            data: event.body.data,
            type: 'PROJECT',
            userId: String(userId)
          }
        });
        break;
      case 'failingProject':
        this.store.dispatch({
          type: 'WS_MESSAGE',
          payload: {
            data: event.body.data,
            type: 'FAILING_PROJECT',
            userId: String(userId)
          }
        });
        break;
      case 'contest':
        this.store.dispatch({
          type: 'WS_MESSAGE',
          payload: {
            data: event.body.data,
            type: 'CONTEST',
            userId: String(userId)
          }
        });
        break;
    }

    // Once the store is updated then we can let everyone know.
    this.fromServerStream.next(event);
  }
  /* We buffer the user subscriptions and unsubscriptions by 1 second
    * to reduce backend load.
    * It's possible to have subscribed and unsubscriped within that buffer
    * so we remove such sub/unsub (and reverse) manually.
  */
  private subscribeToUserEvents() {
    // Subscribe to existing subscriptions
    const existingSubscriptions = this.userSubscriptions.values();
    if (existingSubscriptions.length) {
      this.sendEvent(generateUserSub(existingSubscriptions));
    }

    if (this.channels.size) {
      this.sendEvent(generateChannelSubMessage(Array.from(this.channels)));
    }

    // Subscribe to any new ones that come in
    this.onlineOfflineSubscription = this.onlineOfflineSubs
      .bufferTime(1000)
      .subscribe(
      (userIds) => {
        const reducedSubscriptions = userIds.reduce(
          (acc: { subs: number[], unsubs: number[] }, current) => {
            let subs = acc.subs;
            let unsubs = acc.unsubs;

            if (current.type === 'sub') {
              if (acc.unsubs.includes(current.id)) {
                unsubs = acc.unsubs.filter(id => id !== current.id);
              } else {
                subs = [...acc.subs, current.id];
              }
            }

            if (current.type === 'unsub') {
              if (acc.subs.includes(current.id)) {
                subs = acc.subs.filter(id => id !== current.id);
              } else {
                unsubs = [...acc.unsubs, current.id];
              }
            }

            return { subs, unsubs };
          },
          { subs: [], unsubs: [] }
        );

        if (reducedSubscriptions.subs.length) {
          this.sendEvent(generateUserSub(reducedSubscriptions.subs));
        }
        if (reducedSubscriptions.unsubs.length) {
          this.sendEvent(generateUserUnsub(reducedSubscriptions.unsubs));
        }
        return;
      }
      );
  }

  public subscribeOnlineOffline(userId: Observable<number>) {
    // Watch the user id stream, calculate watch for changes,
    // subscribe/unsubscribe to any additions/removals respectively
    // and return the user status for the user who is subscribed to.
    // We add `undefined` to the end so we can get the last unsub.
    return userId
      .distinctUntilChanged()
      .concat(Observable.of(undefined))
      .scan(
      (acc: { previous?: number, current?: number }, next) => ({
        current: next,
        previous: acc.current,
      }),
      {
        current: undefined,
        previous: undefined,
      }
      )
      .map(
      ({ previous, current }) => {
        if (previous) {
          this.unsubscribeUserOnlineOffline(previous);
        }
        if (current) {
          this.subscribeUserOnlineOffline(current);
        }
        return current;
      },
    )
  }

  private subscribeUserOnlineOffline(userId: number) {
    // If this user id is new, subscribe.
    if (this.userSubscriptions.multiplicity(userId) === 0) {
      this.onlineOfflineSubs.next({ type: 'sub', id: userId });
    }

    this.userSubscriptions.add(userId);
  }

  private unsubscribeUserOnlineOffline(userId: number) {
    // If this user id is the last one, unsubscribe.
    if (this.userSubscriptions.multiplicity(userId) === 1) {
      this.onlineOfflineSubs.next({ type: 'unsub', id: userId });
    }

    this.userSubscriptions.remove(userId);
  }

  public subscribeChannels(channels: number[]) {
    this.sendEvent(generateChannelSubMessage(channels));
    channels.forEach(channel => this.channels.add(channel));
  }

  public unsubscribeChannels(channels: number[]) {
    console.log('Not implemented');
  }
}



// WEBPACK FOOTER //
// ./src/app/@shared/datastore/websocket/websocket.ts